import * as React from "react";
import "./styles.scss";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { PageWrapper } from "../../components/PageWrapper";
import { Button, Menu, Message } from "semantic-ui-react";
import { useEffect, useState } from "react";
import ThemesTab from "./Tabs/ThemesTab";
import PlayerCardTab from "./Tabs/PlayerCardTab";
import { useQueryParam, usePlayerSettings } from "../../utils";
import { CoachSettings } from "../../interfaces";
import { PlayerCard } from "../../services/bedrock";
import PlayerCardPreview from "./PlayerCardPreview";
import TemplatesTab from "./Tabs/TemplatesTab";
import PlayerCardsTab from "./Tabs/PlayerCardsTab";
import ManageDefaultsModal from "./ManageDefaultsModal";
import Context, { PlayerCardSettingsContext } from "./Contexts";
import MetricsModal from "./MetricsModal";

interface PathProps {}
interface PlayerCardSettingsProps extends RouteComponentProps<PathProps> {
  pageLoading?: boolean;
}

export const PlayerCardSettings: React.FC<PlayerCardSettingsProps> = ({ pageLoading }) => {
  const [showMetricsModal, setShowMetricsModal] = useState<boolean>(false);
  const [metricsWidgetId, setMetricsWidgetId] = useState<number>();
  const context = Context();
  const { PlayerCards, Toast, User } = context;
  const history = useHistory();

  useEffect(() => {
    if (pageLoading || isInitializing) return;

    const params = new URLSearchParams(history.location.search);
    const urlPlayerId = params.get("playerId");
    const urlEdit = params.get("edit");

    const currentInEditMode = PlayerCards.inEditMode;

    if (currentInEditMode) {
      const currentPlayerId = PlayerCards.selectedPlayerCard?.playerId;
      if (currentPlayerId) {
        const targetPlayerIdStr = String(currentPlayerId);
        const targetEditStr = "true";

        if (urlPlayerId !== targetPlayerIdStr || urlEdit !== targetEditStr) {
          params.set("playerId", targetPlayerIdStr);
          params.set("edit", "true");
          history.replace({ search: params.toString() });
        }
      }
    } else {
      const activeFilterPlayerId = PlayerCards.filters?.playerId;

      if (activeFilterPlayerId) {
        const targetPlayerIdStr = String(activeFilterPlayerId);
        if (urlPlayerId !== targetPlayerIdStr || urlEdit) {
          params.set("playerId", targetPlayerIdStr);
          params.delete("edit");
          history.replace({ search: params.toString() });
        }
      } else {
        if (urlPlayerId || urlEdit) {
          params.delete("playerId");
          params.delete("edit");
          history.replace({ search: params.toString() });
        }
      }
    }
  }, [
    PlayerCards.selectedPlayerCard?.playerId,
    PlayerCards.inEditMode,
    PlayerCards.filters?.playerId,
    pageLoading,
    isInitializing,
    history,
  ]);

  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [showDefaultsModal, setShowDefaultsModal] = useState<boolean>(false);

  const tabs = [];
  if (User.playerId) {
    tabs.push({ title: "Player Card", key: "playerCard" });
    const { hasSetting } = usePlayerSettings(User.playerId);
    const allowPlayersToEditThemes = hasSetting(CoachSettings.ALLOW_PLAYER_TO_EDIT_PLAYER_CARD_THEMES, false);
    if (allowPlayersToEditThemes) {
      tabs.push({ title: "Themes", key: "themes" });
    }
  } else {
    tabs.push({ title: "Player Cards", key: "playerCards" });
    tabs.push({ title: "Templates", key: "templates" });
    tabs.push({ title: "Themes", key: "themes" });
  }
  const [selectedTab, setSelectedTab] = useQueryParam(
    "tab",
    tabs[0].key,
    tabs.map(tab => tab.key)
  );

  const initializePlayerCard = () => {
    setIsInitializing(true);
    PlayerCards.createPlayerCard().finally(() => setIsInitializing(false));
  };

  useEffect(() => {
    if (!PlayerCards.isLoading) {
      setIsInitializing(false);
      if (!User.isCoach) {
        PlayerCards.setSelectedPlayerCard(PlayerCards.playerCards[0]);
      }
    }
  }, [PlayerCards.isLoading]);

  const handleCloseDefaultsModal = () => {
    setShowDefaultsModal(false);
    PlayerCards.loadPage();
  };

  const handleOpenMetricsModal = (playerCardWidgetId?: number) => {
    setMetricsWidgetId(playerCardWidgetId);
    setShowMetricsModal(true);
  };

  const handleCloseMetricsModal = () => {
    setShowMetricsModal(false);
  };

  return (
    <PlayerCardSettingsContext.Provider value={context}>
      <PageWrapper
        title="Player Cards"
        hideSeparator
        loading={pageLoading || isInitializing}
        className="playerCardSettings"
      >
        <Message hidden={!Toast.toast} className="messageContainer" {...Toast.toast} />

        {!PlayerCards.selectedPlayerCard && !User.isCoach && (
          <div className="createPlayerCard">
            <div className="container">
              <div className="message">It appears you don't have a Player Card yet.</div>
              <Button primary content="Get Started Now!" onClick={initializePlayerCard} />
            </div>
          </div>
        )}
        {(PlayerCards.selectedPlayerCard || User.isCoach) && (
          <>
            <Menu pointing secondary className="tabs">
              {tabs.map(tab => {
                return (
                  <Menu.Item
                    key={`tab-${tab.key}`}
                    name={tab.title}
                    active={selectedTab === tab.key}
                    onClick={() => setSelectedTab(tab.key)}
                  />
                );
              })}
              <div className="grow" />
              {User.isCoach && User.hasWritePermission && (
                <>
                  <ManageDefaultsModal open={showDefaultsModal} onClose={handleCloseDefaultsModal} />
                  <Button
                    className="menuButton"
                    primary
                    content="Manage Defaults"
                    onClick={() => setShowDefaultsModal(true)}
                  />
                </>
              )}
            </Menu>

            <div className="tabContent">
              {User.isCoach && (
                <>
                  <TemplatesTab active={selectedTab === "templates"} />
                  <ThemesTab active={selectedTab === "themes"} />
                  <PlayerCardsTab active={selectedTab === "playerCards"} onViewMetrics={handleOpenMetricsModal} />
                </>
              )}
              {!User.isCoach && PlayerCards.selectedPlayerCard && (
                <>
                  <PlayerCardTab
                    active={selectedTab === "playerCard"}
                    playerCard={PlayerCards.selectedPlayerCard}
                    onPlayerCardSaved={playerCard => PlayerCards.setSelectedPlayerCard(playerCard as PlayerCard)}
                    onViewMetrics={handleOpenMetricsModal}
                  />
                  <ThemesTab active={selectedTab === "themes"} />
                </>
              )}
              <PlayerCardPreview onViewMetrics={() => handleOpenMetricsModal()} />
              <MetricsModal
                open={showMetricsModal}
                playerCard={PlayerCards.selectedPlayerCard}
                defaultWidgetId={metricsWidgetId}
                defaultMetricType={metricsWidgetId ? "CLICK" : "VIEW"}
                onClose={handleCloseMetricsModal}
              />
            </div>
          </>
        )}
      </PageWrapper>
    </PlayerCardSettingsContext.Provider>
  );
};
